import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import moment from 'moment';
import intl from '../../shared/intl';

const defaultInitialState = {
	value: {},
	tmpValue: {},
	tmpSelectCount: 0,
	isOpen: false,
	isRightPosition: true,
	userTimeZoneIsSet: false,
	ranges: {
		'Today': {
			startDate: e => e,
			endDate: e => e
		},
		'Today and yesterday': {
			startDate: e => e.add(-1, 'days'),
			endDate: e => e
		},
		'Yesterday': {
			startDate: e => e.add(-1, 'days'),
			endDate: e => e.add(-1, 'days')
		},
		'This week': {
			startDate: e => e.startOf('isoWeek'),
			endDate: e => e.endOf('day')
		},
		'Last week': {
			startDate: e => e.subtract(7, 'day').startOf('isoWeek'),
			endDate: e => e.subtract(7, 'day').endOf('isoWeek')
		},
		'Current month': {
			startDate: e => e.startOf('month'),
			endDate: e => e.endOf('day')
		},
		'Last month': {
			startDate: e => e.subtract(1, 'month').startOf('month'),
			endDate: e => e.subtract(1, 'month').endOf('month')
		},
		'This year': {
			startDate: e => e.startOf('year'),
			endDate: e => e.endOf('day')
		}
	}
};

const createCalendar = ({
	namespace,
	stateNodeName,
	defaultValue
}) => {
	// action types
	const TOGGLE = `${namespace}/${stateNodeName}-toggle`;
	const CHANGE = `${namespace}/${stateNodeName}-change`;
	const APPLY = `${namespace}/${stateNodeName}-apply`;
	const SET_USER_TIMEZONE_DATE = `${namespace}/${stateNodeName}-set-user-timezone-date`;

	// init state
	const initialState = {
		...defaultInitialState,
		value: defaultValue || {}
	};

	// actions
	const actions = {
		[`${stateNodeName}Init`]: () => dispatch => {
			const action = actions[`${stateNodeName}SetUserTimezoneDate`];
			dispatch(action());
		},
		[`${stateNodeName}Toggle`]: () => ({ type: TOGGLE }),
		[`${stateNodeName}Change`]: (date, onApply) => (dispatch, getState) => {
			const state = getState()[namespace][stateNodeName];

			dispatch({
				type: CHANGE,
				payload: date
			});

			if (state.tmpSelectCount === 2) {
				const applyAction = actions[`${stateNodeName}Apply`];
				dispatch(applyAction());
				onApply();
			}
		},
		[`${stateNodeName}Apply`]: date => ({
			type: APPLY,
			payload: date
		}),
		[`${stateNodeName}SetUserTimezoneDate`]: () => (dispatch, getState) => {
			const { root: { data: { timezone } } } = getState();
			const { value: { start, end }, userTimeZoneIsSet } = getState()[namespace][stateNodeName];

			const convertDateToZoneDateFn = (date, timezone) => {
				const iso = moment(date).format('YYYY-MM-DD');
				const localOffset = moment().tz(moment.tz.guess()).format('THH:mm:ssZ');
				return moment(iso + localOffset)
					.tz(timezone)
					.format('YYYY-MM-DD');
			};

			if (!userTimeZoneIsSet) {
				dispatch({
					type: SET_USER_TIMEZONE_DATE,
					payload: {
						start: convertDateToZoneDateFn(start, timezone),
						end: convertDateToZoneDateFn(end, timezone)
					}
				});
			}

			return userTimeZoneIsSet;
		}
	};

	// reducers
	const reducers = {
		[TOGGLE]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				tmpSelectCount: 0,
				isOpen: !state[stateNodeName].isOpen
			}
		}),
		[CHANGE]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				tmpSelectCount: ++state[stateNodeName].tmpSelectCount,
				tmpValue: {
					start: action.payload.startDate.format('YYYY-MM-DD'),
					end: action.payload.endDate.format('YYYY-MM-DD')
				}
			}
		}),
		[APPLY]: (state, action) => ({
			...state,
			[stateNodeName]: {
				...state[stateNodeName],
				tmpSelectCount: 0,
				isOpen:false,
				value: isEmpty(state[stateNodeName].tmpValue) ?
					state[stateNodeName].value : { ...state[stateNodeName].tmpValue }
			}
		}),
		[SET_USER_TIMEZONE_DATE]: (state, action) => {
			return {
				...state,
				[stateNodeName]: {
					...state[stateNodeName],
					value: action.payload,
					userTimeZoneIsSet: true
				}
			};
		}
	};

	// selectors
	const selectors = {
		getCalendar: (calendarSelector, appState) => createSelector(
			calendarSelector,
			appState,
			(calendar, appState) => {
				const { start, end } = calendar.value;

				const dateRange = values({
					start: moment(start).format('DD.MM.YYYY'),
					end: moment(end).format('DD.MM.YYYY')
				});
				const startDate = moment(start);
				const endDate = moment(end);
				const translator = intl(appState);

				const ranges = {};

				Object.keys(calendar.ranges).forEach(key => {
					ranges[[translator(key)]] = calendar.ranges[key];
				});

				return {
					...calendar,
					ranges,
					dateRange,
					startDate,
					endDate
				};
			}
		)
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default  createCalendar;

export const getDateRangeFromCalendar = calendar => {
	let date_range;

	if (calendar.value && calendar.value.start) {
		date_range = calendar.value;
	}

	if (date_range && date_range.start && date_range.end) {
		let dateStart = moment(date_range.start).hours(0).minutes(0).seconds(0).milliseconds(0);
		let dateEnd = moment(date_range.end).hours(23).minutes(59).seconds(59).milliseconds(999);
		date_range.start = dateStart.format('YYYY-MM-DD HH:mm:ss.SSS');
		date_range.end = dateEnd.format('YYYY-MM-DD HH:mm:ss.SSS');
	}

	return date_range;
};

export const getUtcDateRange = date_range => {
	const d = { ...date_range };

	if (date_range && date_range.start && date_range.end) {
		let dateStart = moment.utc(d.start).hours(0).minutes(0).seconds(0).milliseconds(0);
		let dateEnd = moment.utc(d.end).hours(23).minutes(59).seconds(59).milliseconds(999);
		d.start = dateStart.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
		d.end = dateEnd.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
	}

	return d;
};

export const getDateRangeWithTimeZoneFromCalendar = calendar => {
	const date_range = calendar.value;

	if (date_range && date_range.start && date_range.end) {
		date_range.start = moment(date_range.start).startOf('day').format('YYYY-MM-DD HH:mm:ss.SSSZ');
		date_range.end = moment(date_range.end).endOf('day').format('YYYY-MM-DD HH:mm:ss.SSSZ');
	}

	return date_range;
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/shared/calendar/index.js